// src/pages/ProfilePage.js
import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { getMemberProfile, updateMemberProfile } from '../api';
import NavBar from '../components/shared/NavBar';

const NAVY = '#1B3A6B';
const GOLD = '#C9A84C';

export default function ProfilePage() {
  const { memberId }            = useParams();
  const navigate                = useNavigate();
  const { member: self, role }  = useAuth();

  const targetId     = memberId || self?.id;
  const isOwnProfile = !memberId || memberId === self?.id;
  const canEdit      = isOwnProfile ||
                       role === 'executive' ||
                       role === 'house_rep';

  const [profile, setProfile] = useState(null);
  const [editing, setEditing] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving,  setSaving]  = useState(false);
  const [error,   setError]   = useState('');
  const [success, setSuccess] = useState('');
  const [form,    setForm]    = useState({});

  useEffect(() => {
    async function load() {
      setLoading(true);
      try {
        const result = await getMemberProfile(targetId);
        setProfile(result.profile);
        setForm(result.profile);
      } catch (err) {
        setError('Could not load profile: ' + err.message);
      } finally {
        setLoading(false);
      }
    }
    if (targetId) load();
  }, [targetId]);

  const handleChange = (field, value) => {
    setForm(prev => ({ ...prev, [field]: value }));
  };

  const handleSave = async () => {
    setSaving(true);
    setError('');
    setSuccess('');
    try {
      await updateMemberProfile({ memberId: targetId, ...form });
      setProfile({ ...form });
      setEditing(false);
      setSuccess('Profile updated successfully.');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError('Failed to save: ' + err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    setForm({ ...profile });
    setEditing(false);
    setError('');
  };


  const getInitials = (name) => {
    if (!name) return '?';
    const parts = name.trim().split(' ').filter(Boolean);
    if (parts.length === 1) return parts[0][0].toUpperCase();
    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  };

  const getRoleBadge = (r) => {
    const map = {
      executive: { label: 'Executive', bg: '#fdecea', color: '#c0392b' },
      treasurer: { label: 'Treasurer', bg: '#fff3cd', color: '#856404' },
      house_rep: { label: 'House Rep', bg: '#d4edda', color: '#1a6b3a' },
      member:    { label: 'Member',    bg: '#d6eaf8', color: '#1B3A6B' }
    };
    return map[r] || map.member;
  };

  if (loading) {
    return (
      <div style={{
        minHeight: '100vh', display: 'flex',
        alignItems: 'center', justifyContent: 'center',
        background: '#f0f4f8', fontFamily: 'Arial, sans-serif'
      }}>
        <p style={{ color: '#666' }}>Loading profile...</p>
      </div>
    );
  }

  const badge = getRoleBadge(profile?.role);

  return (
    <div style={{
      minHeight: '100vh', background: '#f0f4f8',
      fontFamily: 'Arial, sans-serif'
    }}>

      {/* Nav Bar */}
      <NavBar />

      {/* Page Content */}
      <div style={{
        maxWidth: '700px', margin: '0 auto',
        padding: '24px 16px'
      }}>

        {/* Back button */}
        {!isOwnProfile && (
          <button
            onClick={() => navigate(-1)}
            style={{
              background: 'none', border: 'none',
              color: NAVY, cursor: 'pointer',
              fontSize: '14px', marginBottom: '16px',
              padding: 0, fontWeight: '600'
            }}
          >
            ← Back
          </button>
        )}

        {/* Success */}
        {success && (
          <div style={{
            background: '#d4edda', border: '1px solid #c3e6cb',
            borderRadius: '8px', padding: '12px 16px',
            marginBottom: '16px', color: '#1a6b3a', fontSize: '14px'
          }}>
            ✓ {success}
          </div>
        )}

        {/* Error */}
        {error && (
          <div style={{
            background: '#fdecea', border: '1px solid #f5c6cb',
            borderRadius: '8px', padding: '12px 16px',
            marginBottom: '16px', color: '#c0392b', fontSize: '14px'
          }}>
            {error}
          </div>
        )}

        {/* Profile Card */}
        <div style={{
          background: '#fff', borderRadius: '12px',
          boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
          overflow: 'hidden'
        }}>

          {/* Header */}
          <div style={{
            background: `linear-gradient(135deg, ${NAVY} 0%, #2d5a9e 100%)`,
            padding: '32px 24px',
            display: 'flex', alignItems: 'center',
            gap: '20px', flexWrap: 'wrap'
          }}>
            <div style={{
              width: '80px', height: '80px', borderRadius: '50%',
              background: GOLD, display: 'flex',
              alignItems: 'center', justifyContent: 'center',
              fontSize: '32px', fontWeight: 'bold',
              color: NAVY, flexShrink: 0
            }}>
              {getInitials(profile?.name)}
            </div>
            <div style={{ flex: 1 }}>
              <h2 style={{
                color: '#fff', margin: '0 0 4px',
                fontSize: '22px', fontWeight: 'bold'
              }}>
                {profile?.name}
                {profile?.nickname
                  ? <span style={{
                      color: GOLD, fontSize: '16px',
                      fontStyle: 'italic', marginLeft: '8px'
                    }}>
                      "{profile.nickname}"
                    </span>
                  : null
                }
              </h2>
              {profile?.maritalName
                ? <p style={{
                    color: 'rgba(255,255,255,0.75)',
                    margin: '0 0 6px', fontSize: '14px'
                  }}>
                    née {profile.maritalName}
                  </p>
                : null
              }
              <p style={{
                color: 'rgba(255,255,255,0.85)',
                margin: '0 0 8px', fontSize: '14px'
              }}>
                {profile?.boardingHouse} House
              </p>
              <span style={{
                background: badge.bg, color: badge.color,
                padding: '3px 10px', borderRadius: '12px',
                fontSize: '12px', fontWeight: 'bold'
              }}>
                {badge.label}
              </span>
            </div>
            {canEdit && !editing && (
              <button
                onClick={() => setEditing(true)}
                style={{
                  background: GOLD, color: NAVY,
                  border: 'none', borderRadius: '8px',
                  padding: '8px 20px', cursor: 'pointer',
                  fontWeight: 'bold', fontSize: '14px'
                }}
              >
                ✏️ Edit
              </button>
            )}
          </div>

          {/* Fields */}
          <div style={{ padding: '24px' }}>

            {/* Privacy toggle */}
            {isOwnProfile && (
              <div style={{
                background: '#f0f4f8', borderRadius: '8px',
                padding: '12px 16px', marginBottom: '20px',
                display: 'flex', alignItems: 'center',
                justifyContent: 'space-between',
                flexWrap: 'wrap', gap: '8px'
              }}>
                <div>
                  <div style={{
                    fontWeight: '600', fontSize: '14px', color: NAVY
                  }}>
                    Profile Visibility
                  </div>
                  <div style={{ fontSize: '12px', color: '#666' }}>
                    {form.privacySetting === 'public_to_group'
                      ? 'Visible to all members'
                      : 'Visible to your boarding house only'}
                  </div>
                </div>
                {editing
                  ? <select
                      value={form.privacySetting || 'public_to_group'}
                      onChange={e =>
                        handleChange('privacySetting', e.target.value)
                      }
                      style={{
                        padding: '8px 12px',
                        border: '2px solid #e0e0e0',
                        borderRadius: '8px', fontSize: '14px',
                        outline: 'none', background: '#fff'
                      }}
                    >
                      <option value="public_to_group">All members</option>
                      <option value="house_only">My house only</option>
                    </select>
                  : <span style={{
                      background: form.privacySetting === 'public_to_group'
                        ? '#d4edda' : '#fff3cd',
                      color: form.privacySetting === 'public_to_group'
                        ? '#1a6b3a' : '#856404',
                      padding: '4px 10px', borderRadius: '12px',
                      fontSize: '12px', fontWeight: 'bold'
                    }}>
                      {form.privacySetting === 'public_to_group'
                        ? '🌍 All Members'
                        : '🏠 House Only'}
                    </span>
                }
              </div>
            )}

            {/* Fields grid */}
            <div className="profile-fields-grid" style={{
              display: 'grid',
              gridTemplateColumns: '1fr 1fr',
              gap: '20px'
            }}>
              {[
                { label: 'Nickname',    field: 'nickname',    sensitive: false },
                { label: 'Marital Name', field: 'maritalName', sensitive: false },
                { label: 'Location',    field: 'location',
                  placeholder: 'City, Country', sensitive: false },
                { label: 'Occupation',  field: 'occupation',  sensitive: false },
                { label: 'Hobbies & Interests', field: 'hobbies', sensitive: false },
                { label: 'Phone',       field: 'phone',
                  placeholder: '+233...', sensitive: true,
                  showIf: isOwnProfile || role === 'executive' },
                { label: 'Email',       field: 'email',
                  sensitive: true, readOnly: true,
                  showIf: isOwnProfile || role === 'executive' },
                { label: 'Date of Birth', field: 'dateOfBirth',
                  placeholder: 'DD/MM/YYYY', sensitive: true,
                  showIf: isOwnProfile },
              ].map(f => {
                if (f.sensitive && f.showIf === false) return null;
                if (f.showIf !== undefined && !f.showIf) return null;
                return (
                  <div key={f.field}>
                    <label style={{
                      display: 'block', fontSize: '12px',
                      fontWeight: '600', color: '#888',
                      textTransform: 'uppercase',
                      letterSpacing: '0.5px', marginBottom: '4px'
                    }}>
                      {f.label}
                    </label>
                    {editing && !f.readOnly
                      ? <input
                          type="text"
                          value={form[f.field] || ''}
                          onChange={e =>
                            handleChange(f.field, e.target.value)
                          }
                          placeholder={f.placeholder || ''}
                          style={{
                            width: '100%', padding: '9px 12px',
                            border: '2px solid #e0e0e0',
                            borderRadius: '8px', fontSize: '14px',
                            outline: 'none', boxSizing: 'border-box'
                          }}
                        />
                      : <div style={{
                          fontSize: '15px',
                          color: form[f.field] ? '#333' : '#bbb',
                          fontStyle: form[f.field] ? 'normal' : 'italic',
                          padding: '4px 0'
                        }}>
                          {form[f.field] || 'Not provided'}
                        </div>
                    }
                  </div>
                );
              })}
            </div>

            {/* Save / Cancel */}
            {editing && (
              <div style={{
                display: 'flex', gap: '12px',
                marginTop: '24px', justifyContent: 'flex-end'
              }}>
                <button
                  onClick={handleCancel}
                  style={{
                    padding: '10px 24px',
                    border: '2px solid #e0e0e0',
                    borderRadius: '8px', background: '#fff',
                    cursor: 'pointer', fontSize: '14px',
                    fontWeight: '600', color: '#555'
                  }}
                >
                  Cancel
                </button>
                <button
                  onClick={handleSave}
                  disabled={saving}
                  style={{
                    padding: '10px 24px',
                    background: NAVY, color: '#fff',
                    border: 'none', borderRadius: '8px',
                    cursor: saving ? 'not-allowed' : 'pointer',
                    fontSize: '14px', fontWeight: 'bold',
                    opacity: saving ? 0.7 : 1
                  }}
                >
                  {saving ? 'Saving...' : 'Save Changes'}
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}